import { createElement, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import { Drawer, IconButton, Paper, Stack, Step, StepLabel, Stepper, Typography, useTheme } from "@mui/material";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import OrderAdminDrawer from "@/features/order/components/OrderAdminDrawer";
import AdminMenuItems from "../add-orders/AdminMenuItems";
import AdminPayment from "../add-orders/AdminPayment";
import { useAppDispatch, useAppSelector, useResponsive, useTerminology } from "@/hooks";
import AdminCheckout, { ICheckout } from "../add-orders/AdminCheckout";
import AdminOrderSettings from "../add-orders/AdminOrderSettings";
import AdminScheduleTime from "../add-orders/AdminScheduleTime";
import { useUpdateOrderMutation } from "@/features/organizations/api/ordersApi";
import { OrderStatus, PaymentMethodsType } from "@/types";
import useSnackbar from "@/hooks/useSnackbar";
import { useAddUserOrgOrderMutation } from "@/api/userOrgOrdersApi";
import { getOrderTotal } from "@/utils/order";
import { deleteOrder } from "@/features/order/slice";
import { openSummary } from "@/store/uiSlice";
import AdminSearchCustomer from "../add-orders/component/AdminSearchCustomer";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CreateOrderDrawer = ({ open, onClose }: Props) => {
  const { organizationId } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [openOrder, setOpenOrder] = useState(false);
  const order = useAppSelector((store) => store.order);
  const [updateOrder] = useUpdateOrderMutation();
  const { isMobile } = useResponsive();
  const { palette } = useTheme();
  const [openSnackbar] = useSnackbar();
  const [addUserOrgOrder] = useAddUserOrgOrderMutation();
  const dispatch = useAppDispatch();
  const { t } = useTerminology();
  const organization = useAppSelector((store) => store.organizations[organizationId || ""]);

  const steps = useMemo(() => {
    const baseSteps = [
      {
        id: "info",
        title: "Información",
        description: "Información",
        component: AdminOrderSettings,
      },
      {
        id: "order",
        title: "Orden",
        description: `Agregar ${t("product", 2, true)}`,
        component: AdminMenuItems,
      },
      {
        id: "client",
        title: "Detalles del cliente",
        component: AdminSearchCustomer,
        description: "Cliente",
      },
    ];

    if (order.flow === "pick-up" || order.flow === "pickup") {
      baseSteps.push({
        id: "time",
        title: "Horario",
        description: "Horario",
        component: AdminScheduleTime,
      });
    }

    baseSteps.push(
      {
        id: "payment",
        title: "Método de pago",
        component: AdminPayment,
        description: "Pago",
      },
      {
        id: "checkout",
        title: "Confirmar pedido",
        component: AdminCheckout,
        description: "Confirmar",
      }
    );

    return baseSteps;
  }, [order.flow, t]);

  const handleNext = (values: ICheckout) => {
    //verify if order source, flow and branch is sent in settings
    if (steps[activeStep].id === "info") {
      if (!order.source) {
        openSnackbar("Debe seleccionar un origen del pedido", "error");
        return;
      }

      if (!order.flow) {
        openSnackbar("Debe seleccionar un método de entrega", "error");
        return;
      }

      if (!order.branch) {
        openSnackbar("Debe seleccionar una sucursal", "error");
        return;
      }
    }

    // verify if items are set in order step
    if (steps[activeStep].id === "order") {
      if (order.items.length === 0) {
        openSnackbar(`Debe agregar al menos un ${t("product")} para continuar`, "error");
        return;
      }
    }

    // verify if time is set in time step
    if (steps[activeStep].id === "time") {
      if (!values?.scheduleTime) {
        openSnackbar("Debe seleccionar un horario para continuar", "error");
        return;
      }
    }

    // verify if client is set in client step
    if (steps[activeStep].id === "client") {
      if (!values?.client?.id) {
        openSnackbar("Seleccione un cliente para continuar", "error");
        return;
      }
      if (order.flow === "delivery" && !Number(values?.deliveryFee)) {
        openSnackbar("Ingrese una tarifa de envío para continuar", "error");
        dispatch(openSummary());
        return;
      }
    }

    // verify if payment method is set in payment step
    if (steps[activeStep].id === "payment") {
      if (!values?.paymentDetails?.method) {
        openSnackbar("Seleccione un método de pago para continuar", "error");
        return;
      }
      if (values?.paymentDetails?.method === PaymentMethodsType.CASH && !values?.paymentDetails?.value) {
        openSnackbar("Ingrese el monto con el que paga el cliente para continuar", "error");
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleOpenOrder = () => {
    setOpenOrder(!openOrder);
  };

  const handleSubmit = async (values: ICheckout, { resetForm }) => {
    if (!organizationId || !order?.id) {
      return;
    }

    const payload = {
      organizationId: organizationId,
      order: {
        ...order,
        ...values,
        status: OrderStatus.PREPARING,
        orderedAt: Date.now(),
      },
    }

    const updateOrderResponse = await updateOrder(payload);

    if (!updateOrderResponse?.error) {
      openSnackbar("Orden completada", "success");

      const total = getOrderTotal(order.items, Boolean(order.taxes));

      const userOrgOrder = {
        organizationId,
        userId: values.client.id,
        orderId: order.id,
        orderedAt: updateOrderResponse?.data?.orderedAt || Date.now(),
        total,
      };

      const userOrgOrderResponse = addUserOrgOrder(userOrgOrder);

      if (!userOrgOrderResponse?.error) {
        await dispatch(deleteOrder({}));
        setActiveStep(0);
        resetForm();
        onClose();
      }
    } else {
      openSnackbar("Error al completar la orden", "error");
    }
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: isMobile ? "100%" : "50%",
            bgcolor: "#DCEAFD",
          },
        },
      }}
    >
      <Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            padding: 2,
            paddingBottom: 2,
          }}
        >
          <Typography variant="h2">Nuevo Pedido</Typography>
          <Paper
            sx={{
              borderRadius: "50%",
            }}
          >
            <IconButton onClick={onClose}>
              <HugeiconsIcon icon={Cancel01Icon} color={palette.primary.main} />
            </IconButton>
          </Paper>
        </Stack>
        <Stack spacing={2} flex={1}>
          <Formik<ICheckout>
            enableReinitialize
            initialValues={
              {
                client: {
                  name: "",
                  lastname: "",
                  phone: "",
                  email: "",
                },
                scheduleTime: null,
                deliveryFee:
                  order.flow === "delivery" && organization?.settings?.services?.delivery?.type === "fixed" ? organization?.settings?.services?.delivery?.fee?.value || 0 : 0,
              } as ICheckout
            }
            onSubmit={handleSubmit}
          >
            <Form
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <Stack
                spacing={2}
                padding={3}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  paddingBottom: "200px",
                }}
              >
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map(({ title, description }) => {
                    const stepProps: { completed?: boolean } = {};
                    const labelProps: {
                      optional?: React.ReactNode;
                    } = {};

                    return (
                      <Step key={title} {...stepProps}>
                        <StepLabel
                          {...labelProps}
                          sx={{
                            "& .MuiStepLabel-iconContainer": {
                              "& .MuiStepIcon-root": {
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                              },
                              "& .MuiStepIcon-root.Mui-completed": {
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                color: palette.success.main,
                              },
                              "& .MuiStepIcon-root.Mui-active": {
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                              },
                            },
                          }}
                        >
                          {description}
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>

                <Stack gap={2} pb={"100px"}>
                  {createElement(steps[activeStep].component)}
                </Stack>
              </Stack>

              <OrderAdminDrawer isOpen={true} onClose={handleOpenOrder} onBack={handleBack} onNext={handleNext} steps={steps} activeStep={activeStep} />
            </Form>
          </Formik>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default CreateOrderDrawer;
